import { EMPTY, fromEvent, merge, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, mapTo, share, switchMap } from 'rxjs/operators';
import { UndoService } from '../..';
import { createMutationObserver } from '../../helpers/dom/browsingContext';
import { getElementContents } from '../../helpers/dom/element';
import { findAncestor, isElement, isNode } from '../../helpers/dom/node';
import { getEmotion } from '../../helpers/emotion';
import { fromMouseTracking, MouseTrackingController } from '../../helpers/fromMouseTracking';
import { isImageElement } from '../../helpers/image';
import {
  createPointsWithSelectionInElement,
  updateSelectionInElementWithPoints,
} from '../../helpers/selection';

const MINIMUM_IMAGE_WIDTH = 10; // px

const WRAPPER_CLASS_NAME = 'editor-image-resize-wrapper';
const HANDLE_CLASS_NAME = 'editor-image-resize-handle';

enum POSITION_HANDLE_CLASS_NAMES {
  TOP_LEFT = 'editor-image-resize-handle-top-left',
  BOTTOM_LEFT = 'editor-image-resize-handle-bottom-left',
  TOP_RIGHT = 'editor-image-resize-handle-top-right',
  BOTTOM_RIGHT = 'editor-image-resize-handle-bottom-right',
}
export function createHtmlImageResizeEffect(
  element: Element,
  isImageResizable: (image: HTMLImageElement) => boolean,
  undoService: UndoService,
): Observable<unknown> {
  const document = element.ownerDocument;
  if (!document) {
    return EMPTY;
  }

  const window = document.defaultView;
  if (!window) {
    return EMPTY;
  }

  // Stream of inline images that are clicked.
  const imageUnderClick$ = merge(
    fromEvent<MouseEvent>(element, 'click').pipe(
      map(event => {
        const { target } = event;
        if (!isNode(target) || event.button !== 0) {
          return null;
        }

        if (isImageElement(target)) {
          return target;
        }

        const wrapper = findAncestor(target, isImageResizeWrapper, element);
        if (wrapper) {
          return wrapper.querySelector('img');
        }

        return null;
      }),
    ),
    // Remove resize handler when receiving a keyboard/blur event, so we don't leak markup/CSS (ie: handles, blue outline).
    fromEvent(element, 'keydown').pipe(mapTo(null)),
    fromEvent(element, 'blur').pipe(mapTo(null)),
    fromEvent(element, 'contextmenu').pipe(mapTo(null)),
  ).pipe(
    distinctUntilChanged(),
    filter(image => !image || (isImageInline(window, image) && isImageResizable(image))),
  );

  return merge(
    // Decorate the clicked image, while we have a subscriber.
    // Note: don't debounce this, because we need synchronous handling for the keydown event.
    imageUnderClick$.pipe(
      switchMap(image => {
        if (!image) {
          return EMPTY;
        }
        const resumeTakingSnapshots = undoService.pauseTakingSnapshots();
        const wrapper = wrapImageForResize(image);
        if (!wrapper) {
          resumeTakingSnapshots();
          return EMPTY;
        }
        const endResizeMode = (obs: MutationObserver) => {
          obs.disconnect();
          forceUnwrapImage(element);
          resumeTakingSnapshots();
        };
        return new Observable(() => {
          // If for some reason, image loses focus without imageUnderClick$ detecting it,
          // (hard to reproduce but possible by clicking the image, right clicking it, copying it, right clicking, pasting),
          // and some mutation is introduced in the editor, force remove the wrapper.
          const observer = createMutationObserver(element, (mutations, obs) => endResizeMode(obs));
          observer.observe(element, {
            childList: true,
            subtree: true,
          });
          return () => endResizeMode(observer);
        });
      }),
    ),
    fromMouseTracking(element, document, window, event => {
      const { target } = event;
      if (!isNode(target) || !isImageResizeHandle(target) || !target.parentElement) {
        return undefined;
      }

      const image = target.parentElement.querySelector('img');
      if (!image) {
        return undefined;
      }

      const handlePosition = Object.values(POSITION_HANDLE_CLASS_NAMES).find(position =>
        target.classList.contains(position),
      );
      if (!handlePosition) {
        return undefined;
      }

      return new HtmlImageResizeController(image, event, handlePosition);
    }),
  ).pipe(share());
}

function isImageInline(window: Window, image: HTMLImageElement): boolean {
  return window.getComputedStyle(image).display === 'inline';
}

function isImageResizeWrapper(node: Node): node is Element {
  return isElement(node) && node.tagName === 'SPAN' && node.classList.contains(WRAPPER_CLASS_NAME);
}

function isImageResizeHandle(node: Node): node is Element {
  return isElement(node) && node.tagName === 'SPAN' && node.classList.contains(HANDLE_CLASS_NAME);
}

function wrapImageForResize(image: HTMLImageElement): Element | null {
  const { ownerDocument: document, parentNode } = image;
  if (!document || !parentNode || isImageResizeWrapper(parentNode)) {
    return null;
  }

  const savedSelection = createPointsWithSelectionInElement(image);

  const wrapper = document.createElement('span');
  wrapper.classList.add(WRAPPER_CLASS_NAME);

  const { css } = getEmotion(document);
  wrapper.classList.add(css`
    position: relative;
    display: inline-block;
    cursor: default;
    outline: 1px #367fee solid;
    font-size: 0;
  `);

  const genericHandleCss = `
    position: absolute;
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background-color: #367fee;
  `;

  // Generating and appending for handles.
  [
    {
      className: POSITION_HANDLE_CLASS_NAMES.TOP_LEFT,
      style: `${genericHandleCss} top: -4px; left: -4px; cursor: nwse-resize;`,
    },
    {
      className: POSITION_HANDLE_CLASS_NAMES.TOP_RIGHT,
      style: `${genericHandleCss} top: -4px; right: -4px; cursor: nesw-resize;`,
    },
    {
      className: POSITION_HANDLE_CLASS_NAMES.BOTTOM_LEFT,
      style: `${genericHandleCss} bottom: -4px; left: -4px; cursor: nesw-resize;`,
    },
    {
      className: POSITION_HANDLE_CLASS_NAMES.BOTTOM_RIGHT,
      style: `${genericHandleCss} bottom: -4px; right: -4px; cursor: nwse-resize;`,
    },
  ].forEach(handleParams => {
    const handle = document.createElement('span');
    handle.classList.add(HANDLE_CLASS_NAME);
    handle.classList.add(handleParams.className);
    handle.classList.add(
      css`
        ${handleParams.style}
      `,
    );
    wrapper.appendChild(handle);
  });

  parentNode.insertBefore(wrapper, image);
  wrapper.appendChild(image);

  updateSelectionInElementWithPoints(image, savedSelection);
  return wrapper;
}

function forceUnwrapImage(element: Element): void {
  element.querySelectorAll(`.${WRAPPER_CLASS_NAME}`).forEach(unwrapImageForResize);
}

function unwrapImageForResize(wrapper: Element): void {
  if (!isImageResizeWrapper(wrapper)) {
    return;
  }

  wrapper.querySelectorAll(`.${HANDLE_CLASS_NAME}`).forEach(handle => handle.remove());
  const { parentNode } = wrapper;
  if (!parentNode) {
    return;
  }

  const savedSelection = createPointsWithSelectionInElement(wrapper);
  const image = wrapper.querySelector('img');

  parentNode.insertBefore(getElementContents(wrapper), wrapper);
  parentNode.removeChild(wrapper);

  if (image !== null) {
    updateSelectionInElementWithPoints(image, savedSelection);
  }
}

class HtmlImageResizeController implements MouseTrackingController {
  constructor(
    image: HTMLImageElement,
    mouseDownEvent: MouseEvent,
    handlePosition: POSITION_HANDLE_CLASS_NAMES,
  ) {
    this.image = image;
    this.handlePosition = handlePosition;

    // Collect image metrics, which is a potentially expensive operation.
    const imageRect = image.getBoundingClientRect();
    this.state = {
      // The corner opposite to the grabbed handle.
      imageX: this.isRightHandle() ? imageRect.left : imageRect.right,
      imageY: this.isBottomHandle() ? imageRect.top : imageRect.bottom,
      // The mouse offset to the image's corner under the grabbed corner.
      mouseOffsetX: mouseDownEvent.clientX - imageRect.left - (this.isRightHandle() ? imageRect.width : 0),
      mouseOffsetY: mouseDownEvent.clientY - imageRect.top - (this.isBottomHandle() ? imageRect.height : 0),
      // The aspect ratio of the image.
      imageRatio:
        image.naturalWidth && image.naturalHeight
          ? image.naturalWidth / image.naturalHeight
          : imageRect.width / imageRect.height,
    };
  }

  private readonly handlePosition: POSITION_HANDLE_CLASS_NAMES;
  private readonly image: HTMLImageElement;
  private state: {
    imageX: number;
    imageY: number;
    mouseOffsetX: number;
    mouseOffsetY: number;
    imageRatio: number;
  };

  private isRightHandle() {
    return (
      [POSITION_HANDLE_CLASS_NAMES.BOTTOM_RIGHT, POSITION_HANDLE_CLASS_NAMES.TOP_RIGHT].indexOf(
        this.handlePosition,
      ) !== -1
    );
  }

  private isBottomHandle() {
    return (
      [POSITION_HANDLE_CLASS_NAMES.BOTTOM_RIGHT, POSITION_HANDLE_CLASS_NAMES.BOTTOM_LEFT].indexOf(
        this.handlePosition,
      ) !== -1
    );
  }

  private calculateMouseDiagonal(event: MouseEvent): number {
    const { mouseOffsetX, mouseOffsetY, imageX, imageY } = this.state;

    // Correct for the mouse offset to the exact bottom right corner.
    const mouseX = event.clientX - mouseOffsetX;
    const mouseY = event.clientY - mouseOffsetY;

    // Calculate the width and height of the rectangle between mouse position and handle's opposite corner.
    const widthFromMouseX = (mouseX - imageX) * (this.isRightHandle() ? 1 : -1);
    const heightFromMouseY = (mouseY - imageY) * (this.isBottomHandle() ? 1 : -1);

    return widthFromMouseX ** 2 + heightFromMouseY ** 2;
  }

  private calculateSize(
    mouseDiagonal: number,
  ): {
    height: number;
    width: number;
  } {
    const { image } = this;
    const { imageRatio } = this.state;

    // Mouse width is the width of a rectangle with the same aspect ratio and whose diagonal of previous rectangle.
    const mouseWidth = imageRatio * Math.sqrt(mouseDiagonal / (imageRatio ** 2 + 1));

    // Ensure the width is not below the minimum we set for any image.
    const width = Math.round(Math.max(MINIMUM_IMAGE_WIDTH, mouseWidth));
    // Calculate the height from the width and aspect ratio.
    const height = width / imageRatio;

    // If the image has a max height, ensure we don't eclipse it.
    const maxHeight = Number(image.dataset.editorMaxHeight) || Number.POSITIVE_INFINITY;
    const isTallerThanMaxHeight = Number.isFinite(maxHeight) && height > maxHeight;
    if (isTallerThanMaxHeight) {
      // Technically this could result in a width below MINIMUM_IMAGE_WIDTH, but that chance is small.
      return {
        height: maxHeight,
        width: maxHeight * imageRatio,
      };
    }

    return { height, width };
  }

  getCursorStyle() {
    return [POSITION_HANDLE_CLASS_NAMES.BOTTOM_RIGHT, POSITION_HANDLE_CLASS_NAMES.TOP_LEFT].indexOf(
      this.handlePosition,
    ) !== -1
      ? 'nwse-resize'
      : 'nesw-resize';
  }

  onLayer(layer: Element) {
    const { css } = getEmotion(layer.ownerDocument);
    layer.classList.add(css`
      cursor: ${this.getCursorStyle()};
    `);
  }

  onMouseMove(event: MouseEvent) {
    const mouseDiagonal = this.calculateMouseDiagonal(event);
    const { height, width } = this.calculateSize(mouseDiagonal);

    this.image.style.width = `${width}px`;
    this.image.style.height = `${height}px`;
  }
}
